import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { btnPrimary, overline } from "@/components/kit";
import { ArrowRight } from "lucide-react";

const CHAPTERS = [
  { n: "01", title: "Une seule obsession : l'entreprise", text: "Nous ne faisons qu'un métier : l'événementiel B2B. Pas de mélange des genres. Cette spécialisation est notre force et votre garantie." },
  { n: "02", title: "La stratégie avant la scénographie", text: "Un bel événement qui ne sert aucun objectif est un échec. Nous partons de vos enjeux business pour concevoir des expériences qui produisent des résultats mesurables." },
  { n: "03", title: "L'exigence dans chaque détail", text: "La différence entre un bon et un grand événement se joue dans les détails invisibles. Régie au cordeau, anticipation des imprévus, sens du service." },
  { n: "04", title: "Des partenaires, pas des prestataires", text: "Nous nous engageons dans la durée avec chaque client et chaque partenaire. C'est cette fidélité qui fait la qualité de nos événements." },
];

export default function APropos() {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {}); }, []);
  const stats = [
    settings.stats_events && { value: settings.stats_events, label: "événements organisés" },
    settings.stats_years && { value: settings.stats_years, label: "ans d'expérience" },
    settings.stats_zone && { value: settings.stats_zone, label: "zone d'intervention" },
  ].filter(Boolean);
  return (
    <>
      <Seo title="À propos" description="Désir Events, agence événementielle B2B : notre vision, notre méthode et notre engagement au service des entreprises." path="/a-propos" />
      <PageHero
        eyebrow="À propos"
        title={<>Le <span className="italic text-lime-dim">désir</span> comme<br />moteur de création.</>}
        subtitle="Nous mettons notre exigence au service des entreprises qui veulent marquer les esprits."
      />

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
        {CHAPTERS.map((c) => (
          <Reveal key={c.n}>
            <div className="grid items-start gap-6 border-t border-ink/10 py-12 md:grid-cols-12 md:py-16" data-testid={`chapter-${c.n}`}>
              <div className="md:col-span-3"><span className="font-serif text-7xl font-medium text-lime-dim md:text-8xl">{c.n}</span></div>
              <h2 className="font-serif text-3xl font-medium leading-tight text-ink md:col-span-5 md:text-5xl">{c.title}</h2>
              <p className="text-lg leading-relaxed text-ink/60 md:col-span-4">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <div className="border-y border-ink/10 bg-lime py-5 text-ink">
        <Marquee items={["Vision", "Exigence", "Créativité", "Précision", "Engagement"]} className="font-serif text-4xl italic md:text-6xl" />
      </div>

      {/* Fondateur — affiché uniquement si renseigné dans l'admin (Preuves) */}
      {settings.founder_name && (
        <section className="border-b border-ink/10 bg-white" data-testid="founder-section">
          <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
            {settings.founder_photo && (
              <div className="md:col-span-4">
                <Reveal>
                  <img src={settings.founder_photo} alt={`${settings.founder_name} — ${settings.founder_role || "Désir Events"}`} className="aspect-[4/5] w-full rounded-2xl object-cover" />
                </Reveal>
              </div>
            )}
            <div className={settings.founder_photo ? "md:col-span-8" : "md:col-span-12"}>
              <Reveal><span className={overline}>Qui pilote vos événements</span></Reveal>
              <Reveal delay={1}>
                <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                  {settings.founder_name}{settings.founder_role ? <span className="text-ink/50">, {settings.founder_role}</span> : ""}
                </h2>
              </Reveal>
              {settings.founded_year && (
                <Reveal delay={1}><p className="mt-3 text-sm uppercase tracking-[0.2em] text-lime-dim">Désir Events — actif depuis {settings.founded_year}</p></Reveal>
              )}
              {settings.founder_message && (
                <Reveal delay={2}><p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/65">{settings.founder_message}</p></Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
        {stats.length > 0 && (
          <div className="mb-16 grid grid-cols-2 gap-8 md:grid-cols-3" data-testid="apropos-stats">
            {stats.map((st, i) => (
              <Reveal key={st.label} delay={i}>
                <div className="border-l-2 border-lime pl-6">
                  <div className="font-serif text-6xl font-medium text-ink md:text-7xl">{st.value}</div>
                  <div className="mt-2 text-sm text-ink/55">{st.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
        <Reveal delay={1}>
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <p className="max-w-xl font-serif text-3xl text-ink md:text-4xl">Envie de travailler avec nous ?</p>
            <div className="flex gap-3">
              <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary}>Recevoir une première proposition <ArrowRight size={16} /></Link>
              <Link to="/rejoignez-nous" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-white">Nous rejoindre</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
